import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Pencil } from 'lucide-react';
import {
  useBreakdownTemplate,
  useUpdateTemplateContents,
  useUpdateBreakdownTemplate,
} from '@/hooks/useBreakdownTemplates';
import { SubItemCategoriesProvider } from '@/contexts/SubItemCategoriesContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TemplateBreakdownEditor, type EditorHandle } from './editor/TemplateBreakdownEditor';
import { isTemplateEditable } from './templateEditability';

const TEMPLATES_PATH = '/settings/job-type-templates';

export default function TemplateBreakdownEditPage() {
  const { id } = useParams<{ id: string }>();
  const { data: template, isLoading, isError } = useBreakdownTemplate(id);
  const updateM = useUpdateTemplateContents(id ?? '');

  const editorRef = useRef<EditorHandle>(null);
  const [dirty, setDirty] = useState(false);

  // Warn on tab close / reload while there are unsaved edits (mirrors the
  // job-breakdown chrome's beforeunload guard).
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirty) e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // In-app navigation guard. This app uses BrowserRouter (not a data router),
  // so react-router's useBlocker is unavailable; intercept the Back link with a
  // confirm prompt instead.
  const handleBack = useCallback(
    (e: React.MouseEvent) => {
      if (dirty && !window.confirm('Discard unsaved changes?')) {
        e.preventDefault();
      }
    },
    [dirty],
  );

  const handleSave = useCallback(() => {
    if (!editorRef.current) return;
    updateM.mutate(editorRef.current.getContents(), {
      onSuccess: () => {
        // Reset the editor's internal dirty flag so its onDirtyChange effect
        // re-fires (false now, and again on the next edit). Without this the
        // page-level setDirty(false) below would leave Save stuck disabled
        // after subsequent edits.
        editorRef.current?.markSaved();
        setDirty(false);
      },
    });
  }, [updateM]);

  // --- Inline name editing -------------------------------------------------
  // The name is a separate field from the breakdown contents; renaming saves
  // immediately via PATCH (the same endpoint the list-page rename uses, which
  // enforces per-project-type name uniqueness — surfaces 409 on collision).
  const renameM = useUpdateBreakdownTemplate();
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  // Enter/Escape unmount the input, which fires a blur; this ref tells the
  // blur handler to skip its save so Escape cancels and Enter doesn't double-save.
  const suppressBlurSaveRef = useRef(false);

  const startEditName = useCallback(() => {
    setNameValue(template?.name ?? '');
    setNameError(null);
    setIsEditingName(true);
  }, [template?.name]);

  const cancelEditName = useCallback(() => {
    setIsEditingName(false);
    setNameError(null);
  }, []);

  const saveName = useCallback(async () => {
    if (!id || !template) return;
    const trimmed = nameValue.trim();
    if (!trimmed) {
      setNameError('Name is required');
      return;
    }
    if (trimmed === template.name) {
      setIsEditingName(false);
      return;
    }
    try {
      await renameM.mutateAsync({ id, data: { name: trimmed } });
      setIsEditingName(false);
    } catch (e: unknown) {
      setNameError(e instanceof Error ? e.message : String(e));
    }
  }, [id, template, nameValue, renameM]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (isError || !template) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4 text-gray-500">
        <p>Template not found.</p>
        <Link to={TEMPLATES_PATH} className="text-sm text-blue-600 hover:underline">
          Back to templates
        </Link>
      </div>
    );
  }

  if (!isTemplateEditable(template.projectType)) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Link
            to={TEMPLATES_PATH}
            className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to templates
          </Link>
        </div>
        <div className="flex flex-col items-center justify-center h-80 gap-3 rounded-lg border border-dashed border-gray-200 bg-gray-50 text-center">
          <h2 className="text-lg font-semibold text-gray-900">{template.name}</h2>
          <p className="max-w-md text-sm text-gray-500">
            Editing {template.projectType} templates is coming soon. For now, these
            templates can only be created from a real job's breakdown.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] gap-4">
      <div className="flex shrink-0 items-center justify-between gap-4 rounded-lg bg-white px-6 py-4 shadow">
        <div className="flex items-center gap-3 min-w-0">
          <Link
            to={TEMPLATES_PATH}
            onClick={handleBack}
            className="inline-flex shrink-0 items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
          <div className="h-8 w-px shrink-0 bg-gray-200" aria-hidden="true" />
          <div className="min-w-0">
            {isEditingName ? (
              <div className="flex items-center gap-2">
                <Input
                  autoFocus
                  value={nameValue}
                  onChange={(e) => {
                    setNameValue(e.target.value);
                    if (nameError) setNameError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      suppressBlurSaveRef.current = true;
                      saveName();
                    }
                    if (e.key === 'Escape') {
                      suppressBlurSaveRef.current = true;
                      cancelEditName();
                    }
                  }}
                  onBlur={() => {
                    if (suppressBlurSaveRef.current) {
                      suppressBlurSaveRef.current = false;
                      return;
                    }
                    saveName();
                  }}
                  disabled={renameM.isPending}
                  className="h-8 w-64 max-w-full text-base font-semibold"
                  aria-label="Template name"
                />
                {renameM.isPending && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
              </div>
            ) : (
              <button
                type="button"
                onClick={startEditName}
                className="group flex max-w-full items-center gap-1.5 text-left"
                aria-label="Rename template"
              >
                <h2 className="truncate text-lg font-semibold leading-tight text-gray-900">
                  {template.name}
                </h2>
                <Pencil className="h-3.5 w-3.5 shrink-0 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100" />
              </button>
            )}
            {nameError ? (
              <p className="truncate text-xs leading-tight text-red-600">{nameError}</p>
            ) : (
              <p className="truncate text-xs leading-tight text-gray-500">{template.projectType}</p>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-4">
          {dirty && (
            <span className="whitespace-nowrap text-xs text-amber-600">Unsaved changes</span>
          )}
          <Button onClick={handleSave} disabled={!dirty || updateM.isPending}>
            {updateM.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </div>
      </div>

      <div className="flex-1 min-h-0">
        <SubItemCategoriesProvider>
          <TemplateBreakdownEditor
            key={template.id}
            initialSnapshot={template.snapshot}
            projectType={template.projectType}
            onDirtyChange={setDirty}
            editorRef={editorRef}
          />
        </SubItemCategoriesProvider>
      </div>
    </div>
  );
}
